/**
 * CRE Formula Engine API (catalog + manual Calculate).
 * Review / formula-reviews is intentionally not integrated in v1.
 */

import { reportingClient } from "./httpClients/reportingClient";
import { useGlobalStore } from "../context/UserContext/userContext";
import { AUTH_SOURCE } from "../constants/authSource";
import {
  appendCreOrganisationToAiPayload,
  getSelectedCreOrganisationId,
} from "../cre/creOrganisation";

function withOrg(payload) {
  const { authSource, creSelectedOrganisationId, creUser } =
    useGlobalStore.getState();
  if (authSource !== AUTH_SOURCE.CRE) return payload || {};
  const organisationId = getSelectedCreOrganisationId(
    creUser,
    creSelectedOrganisationId,
  );
  return appendCreOrganisationToAiPayload(payload || {}, organisationId);
}

/**
 * @param {{ category?: string }} [opts]
 * @returns {Promise<Array<Record<string, unknown>>>}
 */
export async function getFormulaCatalog(opts = {}) {
  const category = opts?.category ? String(opts.category).trim() : "";
  const endpoint = category
    ? `/api/FormulaEngine/Formulas?category=${encodeURIComponent(category)}`
    : "/api/FormulaEngine/Formulas";

  const { authSource, creSelectedOrganisationId, creUser } =
    useGlobalStore.getState();
  const params = {};
  if (authSource === AUTH_SOURCE.CRE) {
    const organisationId = getSelectedCreOrganisationId(
      creUser,
      creSelectedOrganisationId,
    );
    if (organisationId) params.OrganisationId = organisationId;
  }

  const response = await reportingClient.get(endpoint, { params });
  if (response.data?.success === false) {
    const message =
      response.data?.errorInfo?.message ||
      response.data?.message ||
      "Failed to load formulas";
    throw new Error(message);
  }
  const payload = response.data?.payload;
  return Array.isArray(payload) ? payload : [];
}

/**
 * Manual / correction calculation (no LLM).
 * @param {{
 *   formulaId: string,
 *   inputs?: Record<string, number>,
 *   textInputs?: Record<string, string>,
 *   inputUnits?: Record<string, string>,
 * }} body
 */
export async function calculateFormula(body) {
  const endpoint = "/api/FormulaEngine/Calculate";
  try {
    const response = await reportingClient.post(endpoint, withOrg(body));
    if (response.data?.success === false) {
      const info = response.data?.errorInfo || {};
      const err = new Error(
        info.message || response.data?.message || "Calculation failed",
      );
      err.code = info.code || response.data?.errorCode;
      err.status = response.status;
      err.errorInfo = info;
      throw err;
    }
    return response.data;
  } catch (error) {
    const data = error?.response?.data;
    if (data) {
      const info = data.errorInfo || {};
      const err = new Error(
        info.message || data.message || error.message || "Calculation failed",
      );
      err.code = info.code || data.errorCode;
      err.status = error?.response?.status;
      err.errorInfo = info;
      throw err;
    }
    throw error;
  }
}
